import { useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Percent, Trash2, Pencil } from "lucide-react";
import {
  usePromotions,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
  useProducts,
  useCategories,
  PromotionRecord,
  PromotionDiscountType,
} from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";

const DISCOUNT_TYPE_LABEL: Record<PromotionDiscountType, string> = {
  PERCENTAGE: "Porcentaje",
  FIXED: "Monto fijo",
};

function formatValue(promotion: PromotionRecord): string {
  return promotion.discountType === "PERCENTAGE" ? `${Number(promotion.value)}%` : `$${Number(promotion.value).toLocaleString("es-CO")}`;
}

function formatValidity(promotion: PromotionRecord): string {
  if (!promotion.startDate && !promotion.endDate) return "Siempre vigente";
  return `${promotion.startDate ?? "sin inicio"} — ${promotion.endDate ?? "sin fin"}`;
}

/**
 * Promociones/descuentos del POS (tercera pieza de "Inventario avanzado").
 * Cada promoción aplica a UN producto o a TODA una categoría, y se aplica
 * automáticamente al cobrar en el Punto de Venta — no hay que seleccionarla
 * a mano durante la venta.
 */
export default function PromotionsPage() {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PromotionRecord | null>(null);
  const { data: promotions, isLoading } = usePromotions();
  const deletePromotion = useDeletePromotion();

  const handleDelete = async (promotion: PromotionRecord) => {
    if (!window.confirm(`¿Desactivar la promoción "${promotion.name}"?`)) return;
    try {
      await deletePromotion.mutateAsync(promotion.id);
      toast.success("Promoción desactivada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Promociones</h1>
          <p className="text-sm text-gray-500">Descuentos que se aplican automáticamente al cobrar en el Punto de Venta.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva promoción
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5">Aplica a</th>
              <th className="px-4 py-2.5">Descuento</th>
              <th className="px-4 py-2.5">Vigencia</th>
              <th className="px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {promotions?.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-900">{p.name}</td>
                <td className="px-4 py-2.5 text-gray-600">{p.product ? `Producto: ${p.product.name}` : p.category ? `Categoría: ${p.category.name}` : "—"}</td>
                <td className="px-4 py-2.5 text-gray-700">
                  {formatValue(p)} <span className="text-xs text-gray-400">({DISCOUNT_TYPE_LABEL[p.discountType]})</span>
                </td>
                <td className="px-4 py-2.5 text-gray-500">{formatValidity(p)}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.isActive ? "Activa" : "Inactiva"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-2">
                    <button type="button" className="text-gray-400 hover:text-brand-600" onClick={() => setEditing(p)} title="Editar">
                      <Pencil size={14} />
                    </button>
                    {p.isActive && (
                      <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => handleDelete(p)} title="Desactivar">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {promotions?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Todavía no hay promociones creadas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && <PromotionFormModal onClose={() => setShowForm(false)} />}
      {editing && <PromotionFormModal promotion={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PromotionFormModal({ promotion, onClose }: { promotion?: PromotionRecord; onClose: () => void }) {
  const isEditing = !!promotion;
  const [name, setName] = useState(promotion?.name ?? "");
  const [discountType, setDiscountType] = useState<PromotionDiscountType>(promotion?.discountType ?? "PERCENTAGE");
  const [value, setValue] = useState(promotion?.value ?? "");
  const [scope, setScope] = useState<"product" | "category">(promotion?.categoryId ? "category" : "product");
  const [productId, setProductId] = useState(promotion?.productId ?? "");
  const [categoryId, setCategoryId] = useState(promotion?.categoryId ?? "");
  const [startDate, setStartDate] = useState(promotion?.startDate ?? "");
  const [endDate, setEndDate] = useState(promotion?.endDate ?? "");

  const { data: productsPage } = useProducts({ isActive: true, pageSize: 100 });
  const { data: categories } = useCategories(true);
  const createPromotion = useCreatePromotion();
  const updatePromotion = useUpdatePromotion();
  const isPending = createPromotion.isPending || updatePromotion.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    if (!value || Number(value) <= 0) {
      toast.error("El valor del descuento debe ser mayor a cero.");
      return;
    }
    if (scope === "product" && !productId) {
      toast.error("Seleccione un producto.");
      return;
    }
    if (scope === "category" && !categoryId) {
      toast.error("Seleccione una categoría.");
      return;
    }
    const payload = {
      name,
      discountType,
      value,
      productId: scope === "product" ? productId : undefined,
      categoryId: scope === "category" ? categoryId : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    };
    try {
      if (isEditing) {
        await updatePromotion.mutateAsync({ id: promotion.id, ...payload, categoryId: scope === "category" ? categoryId : null, productId: scope === "product" ? productId : null });
        toast.success("Promoción actualizada.");
      } else {
        await createPromotion.mutateAsync(payload);
        toast.success("Promoción creada.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Percent size={16} /> {isEditing ? "Editar promoción" : "Nueva promoción"}
          </h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. 2x1 gaseosas, Descuento lácteos..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo de descuento</label>
              <select className="input-field" value={discountType} onChange={(e) => setDiscountType(e.target.value as PromotionDiscountType)}>
                <option value="PERCENTAGE">Porcentaje (%)</option>
                <option value="FIXED">Monto fijo por unidad</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Valor</label>
              <input className="input-field" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Aplica a</label>
            <div className="mb-2 flex gap-2">
              <button type="button" className={`btn-secondary flex-1 ${scope === "product" ? "border-brand-400 bg-brand-50 text-brand-700" : ""}`} onClick={() => setScope("product")}>
                Un producto
              </button>
              <button type="button" className={`btn-secondary flex-1 ${scope === "category" ? "border-brand-400 bg-brand-50 text-brand-700" : ""}`} onClick={() => setScope("category")}>
                Una categoría
              </button>
            </div>
            {scope === "product" ? (
              <select className="input-field" value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Seleccione un producto...</option>
                {productsPage?.data.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.sku})
                  </option>
                ))}
              </select>
            ) : (
              <select className="input-field" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Seleccione una categoría...</option>
                {categories?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Desde (opcional)</label>
              <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Hasta (opcional)</label>
              <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-gray-400">Si no define fechas, la promoción queda vigente mientras esté activa.</p>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={isPending} onClick={handleSubmit}>
            {isPending ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear promoción"}
          </button>
        </div>
      </div>
    </div>
  );
}
